import React, { useEffect, useState } from 'react';

const defaultCriteria = (variant) => ({
  groupBy: variant === 'customer' ? 'customer' : 'vendor',
  sumByBlanket: false,
  blanketFrom: '',
  blanketTo: '',
  codeFrom: '',
  codeTo: '',
  partnerGroup: 'All',
  propertiesMode: 'Ignore',
  controlAccts: false,
  agingDate: new Date().toISOString().slice(0, 10),
  intervalUnit: 'Days',
  interval1: 30,
  interval2: 60,
  interval3: 90,
  interval4: 120,
  postingFrom: '',
  postingTo: '',
  dueFrom: '',
  dueTo: '',
  docFrom: '',
  docTo: '',
  translateCurrency: false,
  displayZero: false,
  displayReconciled: false,
  ignoreFutureRemit: false,
  displayPages: false,
  considerConnectedVendors: false,
});

function fieldCls() {
  return 'border border-sap-border rounded px-2 py-1 text-xs w-full bg-white focus:outline-none focus:border-sap-blue';
}

function labelCls() {
  return 'text-xs text-sap-textLight whitespace-nowrap';
}

export default function AgingCriteriaModal({
  open,
  variant,
  onClose,
  onApply,
  value,
}) {
  const [form, setForm] = useState(() => ({ ...defaultCriteria(variant), ...value }));

  useEffect(() => {
    if (open) {
      setForm({ ...defaultCriteria(variant), ...value });
    }
  }, [open, variant, value]);

  if (!open) return null;

  const title = variant === 'customer'
    ? 'Customer Receivables Aging - Selection Criteria'
    : 'Vendor Liabilities Aging - Selection Criteria';

  const partnerLabel = variant === 'customer' ? 'Customer Group' : 'Vendor Group';

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleOk = () => {
    onApply(form);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" aria-labelledby="aging-criteria-title">
      <div className="bg-[#E8E8E8] border border-[#B0B0B0] rounded shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#B0B0B0] bg-[#DCDCDC]">
          <h2 id="aging-criteria-title" className="text-xs font-bold text-sap-text">{title}</h2>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-lg leading-none px-1" aria-label="Close">×</button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4 text-sap-text">
          {/* Group by */}
          <div>
            <span className="text-xs font-semibold mr-3">Group By:</span>
            <label className="inline-flex items-center gap-1 mr-4 text-xs">
              <input
                type="radio"
                name="grp"
                checked={variant === 'customer' ? form.groupBy === 'customer' : form.groupBy === 'vendor'}
                onChange={() => set('groupBy', variant === 'customer' ? 'customer' : 'vendor')}
              />
              {variant === 'customer' ? 'Customer' : 'Vendor'}
            </label>
            <label className="inline-flex items-center gap-1 text-xs">
              <input
                type="radio"
                name="grp"
                checked={variant === 'customer' ? form.groupBy === 'sales_employee' : form.groupBy === 'buyer'}
                onChange={() => set('groupBy', variant === 'customer' ? 'sales_employee' : 'buyer')}
              />
              {variant === 'customer' ? 'Sales Employee' : 'Purchasing Agent'}
            </label>
          </div>

          {/* Sum by blanket + blanket range + code */}
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center">
            <label className="flex items-center gap-2 text-xs col-span-2">
              <input type="checkbox" checked={form.sumByBlanket} onChange={(e) => set('sumByBlanket', e.target.checked)} />
              Sum By: <span className="font-medium">Blanket Agreement No.</span>
            </label>
            <span className={labelCls()}>Blanket Agreement No.</span>
            <div className="flex items-center gap-2">
              <span className="text-2xs text-sap-textLight">From</span>
              <input className={fieldCls()} value={form.blanketFrom} onChange={(e) => set('blanketFrom', e.target.value)} />
              <span className="text-2xs text-sap-textLight">To</span>
              <input className={fieldCls()} value={form.blanketTo} onChange={(e) => set('blanketTo', e.target.value)} />
            </div>
            <span className={labelCls()}>Code</span>
            <div className="flex items-center gap-2">
              <span className="text-2xs text-sap-textLight">From</span>
              <input className={fieldCls()} value={form.codeFrom} onChange={(e) => set('codeFrom', e.target.value)} />
              <span className="text-2xs text-sap-textLight">To</span>
              <input className={fieldCls()} value={form.codeTo} onChange={(e) => set('codeTo', e.target.value)} />
            </div>
          </div>

          {/* Partner group + properties + control accts */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={labelCls()}>{partnerLabel}:</span>
              <select className={`${fieldCls()} w-40`} value={form.partnerGroup} onChange={(e) => set('partnerGroup', e.target.value)}>
                <option>All</option>
                <option>Domestic</option>
                <option>Export</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className="px-2 py-0.5 text-2xs border border-sap-border rounded bg-[#E0E0E0] hover:bg-[#D0D0D0]">Properties</button>
              <select className={`${fieldCls()} w-28`} value={form.propertiesMode} onChange={(e) => set('propertiesMode', e.target.value)}>
                <option>Ignore</option>
                <option>Include</option>
              </select>
            </div>
            <label className="inline-flex items-center gap-1 text-xs">
              <input type="checkbox" checked={form.controlAccts} onChange={(e) => set('controlAccts', e.target.checked)} />
              Control Accts
            </label>
            <button type="button" className="px-2 py-0.5 text-2xs border border-sap-border rounded bg-[#E0E0E0]">…</button>
            <button type="button" className="px-2 py-0.5 text-2xs border border-sap-border rounded bg-[#E0E0E0]">Select All</button>
          </div>

          {/* Aging date + interval */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className={labelCls()}>Aging Date</div>
              <input type="date" className={`${fieldCls()} w-40 mt-0.5`} value={form.agingDate} onChange={(e) => set('agingDate', e.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <div>
                <div className={labelCls()}>Interval</div>
                <select className={`${fieldCls()} w-24 mt-0.5`} value={form.intervalUnit} onChange={(e) => set('intervalUnit', e.target.value)}>
                  <option>Days</option>
                  <option>Months</option>
                </select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                {['interval1', 'interval2', 'interval3', 'interval4'].map((k) => (
                  <div key={k} className="text-center">
                    <div className="text-[10px] text-sap-textLight">{form[k]}</div>
                    <input
                      type="number"
                      min={1}
                      className={`${fieldCls()} w-14 text-center`}
                      value={form[k]}
                      onChange={(e) => set(k, parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Date filters */}
          <div className="space-y-1">
            {[
              ['Posting Date', 'postingFrom', 'postingTo'],
              ['Due Date', 'dueFrom', 'dueTo'],
              ['Document Date', 'docFrom', 'docTo'],
            ].map(([lbl, fk, tk]) => (
              <div key={lbl} className="flex items-center gap-2">
                <span className="w-28 text-xs">{lbl}</span>
                <span className="text-2xs text-sap-textLight">From</span>
                <input type="date" className={`${fieldCls()} w-36`} value={form[fk]} onChange={(e) => set(fk, e.target.value)} />
                <span className="text-2xs text-sap-textLight">To</span>
                <input type="date" className={`${fieldCls()} w-36`} value={form[tk]} onChange={(e) => set(tk, e.target.value)} />
              </div>
            ))}
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.translateCurrency} onChange={(e) => set('translateCurrency', e.target.checked)} />
              Translate Leading Currency at Aging Date
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.displayZero} onChange={(e) => set('displayZero', e.target.checked)} />
              {variant === 'customer' ? 'Display Customers with Zero Balance' : 'Display Vendors with Zero Balance'}
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.displayReconciled} onChange={(e) => set('displayReconciled', e.target.checked)} />
              Display Reconciled Transactions
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.ignoreFutureRemit} onChange={(e) => set('ignoreFutureRemit', e.target.checked)} />
              Ignore Future Remit
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.displayPages} onChange={(e) => set('displayPages', e.target.checked)} />
              Display in Pages
            </label>
            {variant === 'customer' && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.considerConnectedVendors} onChange={(e) => set('considerConnectedVendors', e.target.checked)} />
                Consider Connected Vendors
              </label>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[#B0B0B0] bg-[#DCDCDC]">
          <button
            type="button"
            onClick={handleOk}
            className="px-6 py-1.5 text-xs font-semibold bg-[#5C5C5C] text-white border border-[#404040] rounded hover:bg-[#4a4a4a]"
          >
            OK
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-1.5 text-xs font-semibold bg-[#5C5C5C] text-white border border-[#404040] rounded hover:bg-[#4a4a4a]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export { defaultCriteria };
